import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import FileUploadZone, { UploadedFile } from '../components/FileUploadZone'
import { createProject, uploadSource, uploadDocument, addSourceUrl, startProject } from '../api/projects'

interface FileItem extends UploadedFile {
  id: string
  uploading: boolean
  uploaded: boolean
  error?: string
}

interface CloudLink {
  id: string
  url: string
  label: string
  uploading: boolean
  uploaded: boolean
  error?: string
}

const NewProjectPage: React.FC = () => {
  const navigate = useNavigate()
  useAuth() // Ensure user is authenticated
  const [title, setTitle] = useState('')
  const [files, setFiles] = useState<FileItem[]>([])
  const [cloudLinks, setCloudLinks] = useState<CloudLink[]>([])
  const [newLinkUrl, setNewLinkUrl] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFilesAdded = (newFiles: UploadedFile[]) => {
    const items: FileItem[] = newFiles.map((f) => ({
      ...f,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      uploading: false,
      uploaded: false,
    }))
    setFiles((prev) => [...prev, ...items])
  }

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const updateFileLabel = (id: string, label: string) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, label } : f))
    )
  }

  const addCloudLink = () => {
    if (!newLinkUrl.trim()) return

    // Basic URL validation
    if (!newLinkUrl.startsWith('http://') && !newLinkUrl.startsWith('https://')) {
      setError('Ссылка должна начинаться с http:// или https://')
      return
    }

    const link: CloudLink = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      url: newLinkUrl.trim(),
      label: extractLabelFromUrl(newLinkUrl.trim()),
      uploading: false,
      uploaded: false,
    }
    setCloudLinks((prev) => [...prev, link])
    setNewLinkUrl('')
    setError(null)
  }

  const extractLabelFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url)
      const hostname = urlObj.hostname.toLowerCase()

      if (hostname.includes('disk.yandex') || hostname.includes('yadi.sk')) {
        return 'Яндекс.Диск'
      } else if (hostname.includes('cloud.mail.ru')) {
        return 'Mail.ru Cloud'
      } else if (hostname.includes('drive.google')) {
        return 'Google Drive'
      } else if (hostname.includes('dropbox')) {
        return 'Dropbox'
      }

      // Try to get filename from path
      const pathParts = urlObj.pathname.split('/')
      const lastPart = pathParts[pathParts.length - 1]
      if (lastPart && lastPart.length > 0 && lastPart.length < 50) {
        return decodeURIComponent(lastPart)
      }

      return hostname
    } catch {
      return 'Ссылка'
    }
  }

  const removeCloudLink = (id: string) => {
    setCloudLinks((prev) => prev.filter((l) => l.id !== id))
  }

  const updateLinkLabel = (id: string, label: string) => {
    setCloudLinks((prev) =>
      prev.map((l) => (l.id === id ? { ...l, label } : l))
    )
  }

  const handleSubmit = async () => {
    const mediaFiles = files.filter((f) => f.type !== 'document')
    if (mediaFiles.length === 0 && cloudLinks.length === 0) {
      setError('Добавьте хотя бы один файл или ссылку на облако')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      // 1. Create project
      const project = await createProject({
        title: title || `Проект от ${new Date().toLocaleDateString('ru-RU')}`,
      })

      // 2. Upload all files
      for (const file of files) {
        setFiles((prev) =>
          prev.map((f) => (f.id === file.id ? { ...f, uploading: true } : f))
        )

        try {
          if (file.type === 'document') {
            await uploadDocument(project.id, file.file, 'other')
          } else {
            await uploadSource(project.id, file.file, file.label, file.type)
          }

          setFiles((prev) =>
            prev.map((f) =>
              f.id === file.id ? { ...f, uploading: false, uploaded: true } : f
            )
          )
        } catch (err) {
          console.error(`Failed to upload ${file.file.name}:`, err)
          setFiles((prev) =>
            prev.map((f) =>
              f.id === file.id
                ? { ...f, uploading: false, error: 'Ошибка загрузки' }
                : f
            )
          )
        }
      }

      // 3. Add cloud links
      for (const link of cloudLinks) {
        setCloudLinks((prev) =>
          prev.map((l) => (l.id === link.id ? { ...l, uploading: true } : l))
        )

        try {
          await addSourceUrl(project.id, link.url, link.label)

          setCloudLinks((prev) =>
            prev.map((l) =>
              l.id === link.id ? { ...l, uploading: false, uploaded: true } : l
            )
          )
        } catch (err) {
          console.error(`Failed to add link ${link.url}:`, err)
          setCloudLinks((prev) =>
            prev.map((l) =>
              l.id === link.id
                ? { ...l, uploading: false, error: 'Ошибка добавления' }
                : l
            )
          )
        }
      }

      // 4. Start processing
      await startProject(project.id)

      // 5. Navigate to dashboard
      navigate('/dashboard')
    } catch (err) {
      console.error('Failed to create project:', err)
      setError('Не удалось создать проект')
    } finally {
      setIsCreating(false)
    }
  }

  const mediaFiles = files.filter((f) => f.type !== 'document')
  const documentFiles = files.filter((f) => f.type === 'document')
  const hasMediaSources = mediaFiles.length > 0 || cloudLinks.length > 0

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Новый проект</h1>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {/* Title */}
          <div className="mb-6">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Название проекта
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Например: Защита диссертации Иванова"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Media files section */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Аудио/видео файлы
              <span className="text-gray-400 font-normal ml-2">(обязательно)</span>
            </h3>
            <FileUploadZone
              onFilesAdded={handleFilesAdded}
              acceptedFileTypes="media"
              disabled={isCreating}
            />

            {mediaFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {mediaFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-shrink-0">
                      {file.uploading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
                      ) : file.uploaded ? (
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : file.error ? (
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={file.label}
                        onChange={(e) => updateFileLabel(file.id, e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-transparent hover:border-gray-300 rounded focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                        disabled={isCreating}
                      />
                      <p className="text-xs text-gray-400 truncate">{file.file.name}</p>
                    </div>
                    <button
                      onClick={() => removeFile(file.id)}
                      disabled={isCreating || file.uploaded}
                      className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cloud links section */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Ссылки на облако
              <span className="text-gray-400 font-normal ml-2">(альтернатива файлам)</span>
            </h3>
            <p className="text-sm text-gray-500 mb-3">
              Яндекс.Диск, Mail.ru Cloud, Google Drive или прямая ссылка на файл
            </p>

            {/* Input for adding links */}
            <div className="flex gap-2">
              <input
                type="url"
                value={newLinkUrl}
                onChange={(e) => setNewLinkUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCloudLink()}
                placeholder="https://disk.yandex.ru/d/..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={isCreating}
              />
              <button
                onClick={addCloudLink}
                disabled={isCreating || !newLinkUrl.trim()}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Добавить
              </button>
            </div>

            {/* List of added links */}
            {cloudLinks.length > 0 && (
              <div className="mt-4 space-y-2">
                {cloudLinks.map((link) => (
                  <div
                    key={link.id}
                    className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg"
                  >
                    <div className="flex-shrink-0">
                      {link.uploading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
                      ) : link.uploaded ? (
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : link.error ? (
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => updateLinkLabel(link.id, e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-transparent hover:border-gray-300 rounded focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                        disabled={isCreating}
                      />
                      <p className="text-xs text-gray-400 truncate">{link.url}</p>
                    </div>
                    <button
                      onClick={() => removeCloudLink(link.id)}
                      disabled={isCreating || link.uploaded}
                      className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Documents section */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Документы
              <span className="text-gray-400 font-normal ml-2">(опционально)</span>
            </h3>
            <p className="text-sm text-gray-500 mb-3">
              Автореферат, презентация или другие документы помогут улучшить качество транскрипции
            </p>
            <FileUploadZone
              onFilesAdded={handleFilesAdded}
              acceptedFileTypes="documents"
              disabled={isCreating}
            />

            {documentFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {documentFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg"
                  >
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="flex-1 text-sm truncate">{file.file.name}</span>
                    <button
                      onClick={() => removeFile(file.id)}
                      disabled={isCreating}
                      className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={isCreating || !hasMediaSources}
            className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
          >
            {isCreating ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Загрузка...
              </span>
            ) : (
              'Создать проект и начать обработку'
            )}
          </button>
        </div>
      </main>
    </div>
  )
}

export default NewProjectPage
